// Export the project schedule (room schedule + per-unit schedule) to
// Excel (.xlsx) or CSV (.csv). Uses the same room-aggregate logic as the PDF.

import { buildUnitSchedule } from "./unitSchedule";
import { withVariantTags } from "./variantTags";
import { specForUnitModel } from "../data/orderingCodes";
import { buildBaseCode, buildAccessoryCode } from "./orderingCodeBuilder";
import { buildVariantSummaryRows } from "./configurationSchedule";
import { toSI, unitLabel } from "./units";

const fmt = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(d) : "";
};

// SI-aware numeric formatter. `kind` selects the conversion (capacity/flow/
// waterFlow/pressure/temp). When `isSI` is true, values are converted
// IP → SI before formatting; when false, the IP value is emitted as-is.
const fmtU = (v, kind, isSI, d = 0) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "";
  const out = isSI && toSI[kind] ? toSI[kind](n) : n;
  return out.toFixed(d);
};

// Default decimals per kind/system for Excel export.
const decimalsFor = (kind, isSI) => {
  if (kind === "capacity") return 0;
  if (kind === "flow") return isSI ? 1 : 0;
  if (kind === "waterFlow") return isSI ? 3 : 2;
  if (kind === "pressure") return 2;
  if (kind === "temp") return 1;
  return 0;
};

function buildScheduleHeaders(isSI) {
  return [
    "#",
    "Room",
    "Unit",
    "Circuits",
    "Qty",
    `OA (${unitLabel("flow", isSI)})`,
    `EA (${unitLabel("flow", isSI)})`,
    `Total Cooling (${unitLabel("capacity", isSI)})`,
    `PA Sens (${unitLabel("capacity", isSI)})`,
    `PA Lat (${unitLabel("capacity", isSI)})`,
    `Coil Sens (${unitLabel("capacity", isSI)})`,
    `Coil Lat (${unitLabel("capacity", isSI)})`,
    `Heating (${unitLabel("capacity", isSI)})`,
    `Water (${unitLabel("waterFlow", isSI)})`,
    `PD (${unitLabel("pressure", isSI)})`,
    "Status",
  ];
}

function modelLabel(room, raw, isSI = false) {
  // Same heuristic as pdfReport.js modelLabel — pull selected length when
  // available so the schedule shows e.g. "Model S — 4 ft" (or 1200 mm).
  const m = room.unit_model || "";
  const len = raw?.results?.[0]?.length_ft;
  if (!len) return m;
  return isSI ? `${m} — ${Math.round(parseFloat(len) * 300)} mm` : `${m} — ${len} ft`;
}

function roomAgg(room, summary) {
  // PER-UNIT aggregation — matches SummaryTab's per-row rendering. QTY is
  // displayed in its own column; row values must NOT be multiplied by qty
  // (otherwise the Selection Schedule would show zone totals instead of
  // single-unit performance).
  //
  // Exception: a true MIX room (>1 sub-unit with DIFFERENT lengths) still
  // sums across the heterogeneous sub-units, because the row represents
  // the combined room performance in that case.
  const plan = summary.plan || {};
  const qty = plan.qty || 1;
  const units = plan.units && plan.units.length
    ? plan.units
    : [{
        qty,
        per_unit_total: parseFloat(summary.cooling_total_btuh) || 0,
        per_unit_oa: parseFloat(summary.oa_cfm) || parseFloat(room.oa_flow_cfm) || 0,
        result: null,
      }];
  const distinctLengths = new Set(units.map((u) => u.length_ft).filter((l) => l != null));
  const isSplit = units.length > 1 && distinctLengths.size > 1;
  const a = { oa: 0, ea: 0, cool: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, wf: 0, pd: 0 };
  units.forEach((u) => {
    // Non-split rooms: multiplier=1 → per-unit values regardless of QTY.
    // Split rooms: multiplier=sub-unit qty → sums across distinct lengths.
    const sQty = isSplit ? (u.qty || 1) : 1;
    const sub = u.result?.summary || summary;
    const perOa = u.per_unit_oa != null
      ? parseFloat(u.per_unit_oa)
      : (parseFloat(sub.oa_cfm) || parseFloat(room.oa_flow_cfm) || 0);
    a.oa += perOa * sQty;
    a.ea += (parseFloat(sub.ea_cfm) || parseFloat(room.ea_flow_cfm) || 0) * sQty;
    a.cool += (parseFloat(sub.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * sQty;
    a.pa_sens += (parseFloat(sub.pa_sensible_btuh) || 0) * sQty;
    a.pa_lat += (parseFloat(sub.pa_latent_btuh) || 0) * sQty;
    a.coil_sens += (parseFloat(sub.coil_sensible_btuh) || 0) * sQty;
    a.coil_lat += (parseFloat(sub.coil_latent_btuh) || 0) * sQty;
    a.heat += (parseFloat(sub.heating_btuh) || 0) * sQty;
    a.wf += (parseFloat(sub.water_flow_gpm) || 0) * sQty;
    a.pd = Math.max(a.pd, parseFloat(sub.water_pd_fth2o) || 0);
  });
  return a;
}

// Legacy headers (IP). Kept as a fallback when callers don't pass `isSI`.
const SCHEDULE_HEADERS = buildScheduleHeaders(false);

function buildScheduleRows(rooms, results, isSI = false) {
  return rooms.map((r, i) => {
    const res = results[i] || {};
    const s = res.summary || {};
    const plan = s.plan || {};
    const qty = plan.qty || 1;
    const qtyLabel = plan.overridden
      ? `${qty}${plan.units && plan.units.length > 1 ? " (mix)" : ""}`
      : String(qty);
    const ag = roomAgg(r, s);
    const circuits = s.selected_circuits ?? null;
    return [
      i + 1,
      r.name || `Room ${i + 1}`,
      modelLabel(r, res.raw, isSI),
      circuits != null ? String(circuits) : "",
      qtyLabel,
      fmtU(ag.oa, "flow", isSI, decimalsFor("flow", isSI)),
      fmtU(ag.ea, "flow", isSI, decimalsFor("flow", isSI)),
      fmtU(ag.cool, "capacity", isSI, 0),
      ag.pa_sens ? fmtU(ag.pa_sens, "capacity", isSI, 0) : "",
      ag.pa_lat ? fmtU(ag.pa_lat, "capacity", isSI, 0) : "",
      fmtU(ag.coil_sens, "capacity", isSI, 0),
      ag.coil_lat ? fmtU(ag.coil_lat, "capacity", isSI, 0) : "",
      fmtU(ag.heat, "capacity", isSI, 0),
      fmtU(ag.wf, "waterFlow", isSI, decimalsFor("waterFlow", isSI)),
      fmtU(ag.pd, "pressure", isSI, 2),
      res.ok ? "OK" : "FAIL",
    ];
  });
}

function buildTotalsRow(rooms, results, isSI = false) {
  // Mirror SummaryTab's totals loop exactly: iterate plan.units[], accumulate
  // with each sub-unit's own qty, and never re-multiply by plan.qty. This
  // prevents double-counting for split rooms (where roomAgg already sums
  // sub-unit qtys, so an outer plan.qty multiply would be 2×).
  const t = rooms.reduce(
    (a, r, i) => {
      const s = results[i]?.summary || {};
      const plan = s.plan || {};
      const units = plan.units && plan.units.length
        ? plan.units
        : [{ qty: plan.qty ?? 1, per_unit_total: parseFloat(s.cooling_total_btuh) || 0,
             per_unit_oa: parseFloat(s.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0, result: null }];
      const isSingleVariant = units.length === 1;
      const singleVariantQty = plan.qty ?? units[0]?.qty ?? 1;
      units.forEach((u) => {
        const subQty = isSingleVariant ? singleVariantQty : (u.qty || 1);
        const sub = u.result?.summary || s;
        const perOa = u.per_unit_oa != null
          ? parseFloat(u.per_unit_oa)
          : (parseFloat(sub.oa_cfm) || parseFloat(r.oa_flow_cfm) || 0);
        a.qty += subQty;
        a.oa += perOa * subQty;
        a.ea += (parseFloat(sub.ea_cfm) || parseFloat(r.ea_flow_cfm) || 0) * subQty;
        a.cool += (parseFloat(sub.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * subQty;
        a.pa_sens += (parseFloat(sub.pa_sensible_btuh) || 0) * subQty;
        a.pa_lat += (parseFloat(sub.pa_latent_btuh) || 0) * subQty;
        a.coil_sens += (parseFloat(sub.coil_sensible_btuh) || 0) * subQty;
        a.coil_lat += (parseFloat(sub.coil_latent_btuh) || 0) * subQty;
        a.heat += (parseFloat(sub.heating_btuh) || 0) * subQty;
        a.wf += (parseFloat(sub.water_flow_gpm) || 0) * subQty;
      });
      return a;
    },
    { qty: 0, oa: 0, ea: 0, cool: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, wf: 0 }
  );
  return [
    "",
    "Project Totals",
    "",
    "",
    t.qty,
    fmtU(t.oa, "flow", isSI, decimalsFor("flow", isSI)),
    fmtU(t.ea, "flow", isSI, decimalsFor("flow", isSI)),
    fmtU(t.cool, "capacity", isSI, 0),
    t.pa_sens ? fmtU(t.pa_sens, "capacity", isSI, 0) : "",
    t.pa_lat ? fmtU(t.pa_lat, "capacity", isSI, 0) : "",
    fmtU(t.coil_sens, "capacity", isSI, 0),
    t.coil_lat ? fmtU(t.coil_lat, "capacity", isSI, 0) : "",
    fmtU(t.heat, "capacity", isSI, 0),
    fmtU(t.wf, "waterFlow", isSI, decimalsFor("waterFlow", isSI)),
    "",
    "",
  ];
}

function safeFilename(name) {
  return (name || "Project").replace(/[^a-zA-Z0-9-]+/g, "_");
}

// Variant Summary headers + row builder for CSV/XLSX summary blocks.
const VARIANT_SUMMARY_HEADERS = [
  "Tag", "Variant", "Unit Model", "QTY", "Code B (Base)", "Code A (Accessory)",
];

function summaryUnitModelLabel(r, isSI = false) {
  const isLA = r.unit_model === "Model N" || r.unit_model === "Model N-f";
  if (isLA) return r.unit_model;
  if (r.length_ft == null) return r.unit_model;
  const lenStr = isSI
    ? `${Math.round(parseFloat(r.length_ft) * 300)} mm`
    : `${r.length_ft} ft`;
  return `${r.unit_model} · ${lenStr}`;
}

function buildVariantSummaryFlatRows(rooms, results, configurations, isSI = false) {
  const rows = buildVariantSummaryRows({ rooms, results, configurations });
  return rows.map((r) => [
    r.tag,
    r.variantName,
    summaryUnitModelLabel(r, isSI),
    r.qty,
    r.baseCode,
    r.accCode,
  ]);
}

// Build flat per-(room, variant) rows for the configurator-aware schedule.
// Slim 7-column layout: # | Room | Unit Model | QTY | Config variant
//                       | Base config (Code B) | Accessories Config (Code A)
const SCHEDULE_HEADERS_WITH_CONFIG = [
  "#", "Room", "Unit Model", "QTY", "Config variant",
  "Base config (Code B)", "Accessories Config (Code A)",
];

function unitModelLabelForRow(group, isSI = false) {
  const isLengthAgnostic = group.unit_model === "Model N" || group.unit_model === "Model N-f";
  if (isLengthAgnostic) return group.unit_model;
  if (group.length_ft == null) return group.unit_model;
  const lenStr = isSI
    ? `${Math.round(parseFloat(group.length_ft) * 300)} mm`
    : `${group.length_ft} ft`;
  return `${group.unit_model} · ${lenStr}`;
}

// Match an assignment to a room using room_id (schema v3) or room_idx (fallback).
function matchesRoomSE(a, room, roomIdx) {
  if (room.id != null && a.room_id != null) return a.room_id === room.id;
  return a.room_idx === roomIdx; // backward-compat
}

function buildScheduleRowsWithConfig(rooms, results, configurations, isSI = false) {
  const groups = buildUnitSchedule(rooms || [], results || []);
  const tagged = withVariantTags(configurations || [], groups);
  // Must match ConfiguratorTab.groupKey (3-part, includes circuits). See
  // variantTags.js for the same rationale.
  const groupKey = (g) =>
    `${specForUnitModel(g.unit_model)?.id || g.unit_model}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
  // Index groups by room_id (schema v3) and room_idx (fallback).
  const groupByRoomId = new Map();   // room.id → group
  const groupByRoomIdx = new Map();  // positional idx → group (fallback)
  groups.forEach((g) => g.rooms.forEach((r) => {
    if (r.room_id != null) groupByRoomId.set(r.room_id, g);
    groupByRoomIdx.set(r.room_idx, g);
  }));

  const out = [];
  let rowNum = 0;
  rooms.forEach((r, i) => {
    // Prefer stable id lookup; fall back to positional.
    const g = (r.id != null && groupByRoomId.get(r.id)) || groupByRoomIdx.get(i);
    if (!g) {
      // Skip rooms with no successful selection — they can't have a config
      return;
    }
    rowNum += 1;
    const k = groupKey(g);
    const variantsHere = tagged.filter((v) => v.key === k);
    const cells = variantsHere
      .map((v) => {
        const a = (v.assignments || []).find((x) => matchesRoomSE(x, r, i));
        return { variant: v, qty: parseInt(a?.qty, 10) || 0 };
      })
      .filter((x) => x.qty > 0);

    // Resolve the room entry from g.rooms for qty (works with both id and idx).
    const gRoom = g.rooms.find((x) =>
      (r.id != null && x.room_id != null) ? x.room_id === r.id : x.room_idx === i
    );

    const modelLabelStr = unitModelLabelForRow(g, isSI);
    const spec = specForUnitModel(g.unit_model);
    if (cells.length === 0) {
      out.push([
        rowNum,
        r.name || `Room ${i + 1}`,
        modelLabelStr,
        gRoom?.qty || 1,
        "",
        "",
        "",
      ]);
    } else {
      cells.forEach((c, idx) => {
        const baseCode = spec ? buildBaseCode(spec, c.variant.base, c.variant.extras, { unitModel: c.variant.unit_model || r.unit_model }) : "";
        const accCode = spec ? buildAccessoryCode(spec, c.variant.accessory, c.variant.extras) : "";
        out.push([
          idx === 0 ? rowNum : "",
          idx === 0 ? (r.name || `Room ${i + 1}`) : "",
          idx === 0 ? modelLabelStr : "",
          c.qty,
          c.variant.tag,
          baseCode,
          accCode,
        ]);
      });
    }
  });
  return out;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportScheduleCSV({ project, rooms, results, isSI = false }) {
  const headers = buildScheduleHeaders(isSI);
  const rows = [headers, ...buildScheduleRows(rooms, results, isSI), buildTotalsRow(rooms, results, isSI)];
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? "" : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_UnitSchedule_${new Date().toISOString().slice(0, 10)}.csv`
  );
}

// Configurator-aware CSV export: includes a Variant Summary block, a
// Configurations block (per-variant Inclusions / Exclusions, mirroring the
// submittal), and the detailed (room, variant) schedule with full ordering
// codes.
export function exportConfiguredScheduleCSV({ project, rooms, results, configurations, isSI = false }) {
  const summaryFlat = buildVariantSummaryFlatRows(rooms, results, configurations, isSI);
  const summaryTotalQty = summaryFlat.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
  const summaryRows = buildVariantSummaryRows({ rooms, results, configurations });

  const rows = [];
  if (summaryFlat.length) {
    rows.push(["Variant Summary"]);
    rows.push(VARIANT_SUMMARY_HEADERS);
    summaryFlat.forEach((r) => rows.push(r));
    rows.push(["", "", "Total Units", summaryTotalQty, "", ""]);
    rows.push([]);
  }
  if (summaryRows.length) {
    rows.push(["Configurations — Inclusions & Exclusions"]);
    summaryRows.forEach((r) => {
      rows.push([]);
      rows.push([
        `${r.tag} — ${r.variantName} — ${summaryUnitModelLabel(r, isSI)} — Qty ${r.qty}`,
      ]);
      rows.push(["Code B (Base)", r.baseCode]);
      rows.push(["Code A (Accessory)", r.accCode]);
      rows.push(["Includes", "Not included"]);
      const inc = r.inclusions.length ? r.inclusions : ["All standard."];
      const exc = r.exclusions.length ? r.exclusions : ["None."];
      const maxLen = Math.max(inc.length, exc.length);
      for (let i = 0; i < maxLen; i += 1) {
        rows.push([inc[i] || "", exc[i] || ""]);
      }
    });
    rows.push([]);
  }
  rows.push(["Detailed Schedule"]);
  rows.push(SCHEDULE_HEADERS_WITH_CONFIG);
  buildScheduleRowsWithConfig(rooms, results, configurations, isSI).forEach((r) => rows.push(r));

  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? "" : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_Schedule_${new Date().toISOString().slice(0, 10)}.csv`
  );
}

export async function exportScheduleXLSX({ project, rooms, results, isSI = false }) {
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Product Selector";
  wb.created = new Date();

  // ---- Sheet 1: Project Info ----
  const info = wb.addWorksheet("Project Info");
  info.columns = [{ width: 28 }, { width: 40 }];
  const dc = project.designConditions || {};
  const adv = project.advanced || {};
  // Outdoor + Location (city / country / ASHRAE design city / outdoor design
  // temperatures) are only meaningful when the project includes a Model N /
  // N-f (whose PAC draws outdoor air). For room-air / induction-only
  // projects we suppress those rows from the XLS so we don't leak a default
  // "USA / Anchorage" from a legacy load into the export.
  const isModelNLike = (m) => m === "Model N" || m === "Model N-f";
  const needsOutdoor = (rooms || []).some((r) => isModelNLike(r.unit_model));
  const isOtherCountry = dc.country === "__other__";
  const country = isOtherCountry ? (dc.country_other || "") : (dc.country || "");
  const designCity = isOtherCountry
    ? (dc.city_other || "")
    : (dc.city && dc.city !== "__manual__" ? dc.city : "");
  const locationLine =
    [project.cityText, project.state, needsOutdoor ? country : ""].filter(Boolean).join(", ");
  const infoRows = [
    ["Project", project.name || ""],
    ["Number", project.number || ""],
    ["Address", project.address || ""],
    ["City / State" + (needsOutdoor ? " / Country" : ""), locationLine],
    ...(needsOutdoor && designCity ? [["Design Reference City", designCity]] : []),
    ...(needsOutdoor ? [
      ["Outdoor Summer DB / WB (°F)", `${dc.outdoor_db_f || ""} / ${dc.outdoor_wb_f || ""}`],
      ["Outdoor Winter DB (°F)", dc.outdoor_db_winter_f || ""],
    ] : []),
    ["Room DB / RH (Summer)", `${dc.room_db_f || ""} °F / ${dc.room_rh || ""}%`],
    ["Water EWT (Cool / Heat)", `${dc.water_entering_f || ""} / ${dc.water_entering_winter_f || ""} °F`],
    ["Altitude (ft)", dc.altitude_ft || ""],
    ["Glycol", adv.use_glycol ? `${adv.glycol_type} ${adv.glycol_percentage}%` : "None"],
    ["Air-On Cool / Heat (°F)", `${adv.air_on_temp_f || "auto"} / ${adv.air_on_temp_heating_f || "auto"}`],
    ["Generated", new Date().toLocaleString()],
  ];
  infoRows.forEach((r) => {
    const row = info.addRow(r);
    row.getCell(1).font = { bold: true };
  });

  // ---- Sheet 2: Unit Schedule ----
  const sch = wb.addWorksheet("Unit Schedule");
  sch.addRow(buildScheduleHeaders(isSI));
  sch.getRow(1).eachCell((c) => {
    c.font = { bold: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
    c.alignment = { horizontal: "center" };
    c.border = { bottom: { style: "thin", color: { argb: "FF94A3B8" } } };
  });
  buildScheduleRows(rooms, results, isSI).forEach((r) => sch.addRow(r));
  const totalsRow = sch.addRow(buildTotalsRow(rooms, results, isSI));
  totalsRow.eachCell((c) => {
    c.font = { bold: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  });
  sch.columns = [
    { width: 4 }, { width: 22 }, { width: 18 }, { width: 10 }, { width: 6 },
    { width: 10 }, { width: 10 }, { width: 18 },
    { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 },
    { width: 14 }, { width: 12 }, { width: 12 }, { width: 8 },
  ];

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_UnitSchedule_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

// Configurator-aware XLSX export: adds a "Variant Summary" worksheet (one row
// per variant with total qty + Code B/A) followed by the detailed
// "Configured Schedule" worksheet (one row per room/variant assignment).
export async function exportConfiguredScheduleXLSX({ project, rooms, results, configurations, isSI = false }) {
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Product Selector";
  wb.created = new Date();

  // ---- Sheet 1: Variant Summary ----
  const summary = wb.addWorksheet("Variant Summary");
  summary.addRow(VARIANT_SUMMARY_HEADERS);
  summary.getRow(1).eachCell((c) => {
    c.font = { bold: true, color: { argb: "FFFFFFFF" } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    c.alignment = { horizontal: "center", wrapText: true };
  });
  const summaryFlat = buildVariantSummaryFlatRows(rooms, results, configurations, isSI);
  summaryFlat.forEach((r) => summary.addRow(r));
  if (summaryFlat.length) {
    const totalQty = summaryFlat.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
    const totalsRow = summary.addRow(["", "", "Total Units", totalQty, "", ""]);
    totalsRow.eachCell((c) => {
      c.font = { bold: true };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
    });
  }
  summary.columns = [
    { width: 10 },  // Tag
    { width: 22 },  // Variant
    { width: 24 },  // Unit Model
    { width: 8 },   // QTY
    { width: 32 },  // Code B
    { width: 50 },  // Code A
  ];

  // ---- Sheet 2: Configurations (per-variant Inclusions / Exclusions) ----
  const cfgSheet = wb.addWorksheet("Configurations");
  cfgSheet.columns = [{ width: 50 }, { width: 50 }];
  const summaryRows = buildVariantSummaryRows({ rooms, results, configurations });
  summaryRows.forEach((r, idx) => {
    const titleRow = cfgSheet.addRow([
      `${r.tag} — ${r.variantName} — ${summaryUnitModelLabel(r, isSI)} — Qty ${r.qty}`,
      "",
    ]);
    cfgSheet.mergeCells(`A${titleRow.number}:B${titleRow.number}`);
    titleRow.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    titleRow.getCell(1).fill = {
      type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" },
    };
    titleRow.getCell(1).alignment = { vertical: "middle" };
    titleRow.height = 18;

    const codeBRow = cfgSheet.addRow(["Code B (Base)", r.baseCode]);
    codeBRow.getCell(1).font = { bold: true };
    codeBRow.getCell(2).font = { name: "Consolas", color: { argb: "FF92400E" } };
    const codeARow = cfgSheet.addRow(["Code A (Accessory)", r.accCode]);
    codeARow.getCell(1).font = { bold: true };
    codeARow.getCell(2).font = { name: "Consolas", color: { argb: "FF047857" } };

    const hdrRow = cfgSheet.addRow(["Includes", "Not included"]);
    hdrRow.eachCell((c) => {
      c.font = { bold: true, color: { argb: "FFFFFFFF" } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF334155" } };
      c.alignment = { horizontal: "center" };
    });

    const inc = r.inclusions.length ? r.inclusions : ["All standard."];
    const exc = r.exclusions.length ? r.exclusions : ["None."];
    const maxLen = Math.max(inc.length, exc.length);
    for (let i = 0; i < maxLen; i += 1) {
      const dataRow = cfgSheet.addRow([inc[i] || "", exc[i] || ""]);
      dataRow.eachCell((c) => {
        c.alignment = { wrapText: true, vertical: "top" };
        c.border = { top: { style: "hair", color: { argb: "FFE2E8F0" } } };
      });
    }
    if (idx < summaryRows.length - 1) cfgSheet.addRow([]);
  });

  // ---- Sheet 3: Configured Schedule (detailed) ----
  const sch = wb.addWorksheet("Configured Schedule");
  sch.addRow(SCHEDULE_HEADERS_WITH_CONFIG);
  sch.getRow(1).eachCell((c) => {
    c.font = { bold: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
    c.alignment = { horizontal: "center", wrapText: true };
    c.border = { bottom: { style: "thin", color: { argb: "FF94A3B8" } } };
  });
  buildScheduleRowsWithConfig(rooms, results, configurations, isSI).forEach((r) => sch.addRow(r));
  sch.columns = [
    { width: 5 },   // #
    { width: 28 },  // Room
    { width: 22 },  // Unit Model
    { width: 8 },   // QTY
    { width: 14 },  // Config variant
    { width: 32 },  // Base code (B)
    { width: 50 },  // Accessory code (A)
  ];

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_Configured_Schedule_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

// ------------------------------------------------------------------------
// CONFIG FILE export — a focused summary of variants with ordering codes
// (Tag / Variant / Unit Model / QTY / Code B / Code A) in Excel + CSV.
// Used by the Config tab's "Save Config File" button. Distinct from the
// full Configured Schedule export, which also includes per-variant
// inclusions/exclusions and the detailed (room, variant) schedule.
// ------------------------------------------------------------------------
export function exportConfigFileCSV({ project, rooms, results, configurations, isSI = false }) {
  const summaryFlat = buildVariantSummaryFlatRows(rooms, results, configurations, isSI);
  const totalQty = summaryFlat.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
  const rows = [
    ["Project", project.name || ""],
    ["Project Number", project.number || ""],
    ["Generated", new Date().toISOString().slice(0, 10)],
    [],
    VARIANT_SUMMARY_HEADERS,
    ...summaryFlat,
    ["", "", "Total Units", totalQty, "", ""],
  ];
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? "" : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_ConfigFile_${new Date().toISOString().slice(0, 10)}.csv`
  );
}

export async function exportConfigFileXLSX({ project, rooms, results, configurations, isSI = false }) {
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Product Selector";
  const ws = wb.addWorksheet("Config File");
  // Header block
  ws.addRow(["ZAMBA Configuration File"]).font = { bold: true, size: 14 };
  ws.addRow(["Project", project.name || ""]);
  ws.addRow(["Project Number", project.number || ""]);
  ws.addRow(["Generated", new Date().toISOString().slice(0, 10)]);
  ws.addRow([]);
  // Variant Summary table
  const hdr = ws.addRow(VARIANT_SUMMARY_HEADERS);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    c.alignment = { horizontal: "center", vertical: "middle" };
  });
  const summaryFlat = buildVariantSummaryFlatRows(rooms, results, configurations, isSI);
  summaryFlat.forEach((r) => ws.addRow(r));
  const totalQty = summaryFlat.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
  const totalRow = ws.addRow(["", "", "Total Units", totalQty, "", ""]);
  totalRow.font = { bold: true };
  totalRow.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  });
  ws.columns = [
    { width: 10 }, { width: 28 }, { width: 22 }, { width: 8 }, { width: 32 }, { width: 32 },
  ];
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_ConfigFile_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

// ------------------------------------------------------------------------
// SCHEDULE export (per-variant only) — Variant | Model | Length | QTY.
// Used by the Summary tab's "Save Schedule" button. Distinct from:
//   • exportConfigFileCSV/XLSX → also includes Code B / Code A
//   • exportScheduleCSV/XLSX   → full per-room performance schedule
// ------------------------------------------------------------------------
const SUMMARY_SCHEDULE_HEADERS = ["Variant", "Model", "Length", "QTY"];

function buildSummaryScheduleRows(rooms, results, configurations, isSI = false) {
  const rows = buildVariantSummaryRows({ rooms, results, configurations });
  return rows.map((r) => {
    const isLA = r.unit_model === "Model N" || r.unit_model === "Model N-f";
    let lenLabel = "—";
    if (!isLA && r.length_ft != null) {
      lenLabel = isSI
        ? `${Math.round(parseFloat(r.length_ft) * 300)} mm`
        : `${r.length_ft} ft`;
    }
    return [r.tag, r.unit_model, lenLabel, r.qty];
  });
}

export function exportSummaryScheduleCSV({ project, rooms, results, configurations, isSI = false }) {
  const dataRows = buildSummaryScheduleRows(rooms, results, configurations, isSI);
  const totalQty = dataRows.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
  const rows = [
    ["Project", project.name || ""],
    ["Project Number", project.number || ""],
    ["Generated", new Date().toISOString().slice(0, 10)],
    [],
    SUMMARY_SCHEDULE_HEADERS,
    ...dataRows,
    ["", "", "Total Units", totalQty],
  ];
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? "" : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_Schedule_${new Date().toISOString().slice(0, 10)}.csv`
  );
}

export async function exportSummaryScheduleXLSX({ project, rooms, results, configurations, isSI = false }) {
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Product Selector";
  const ws = wb.addWorksheet("Schedule");
  ws.addRow(["ZAMBA Schedule (by Variant)"]).font = { bold: true, size: 14 };
  ws.addRow(["Project", project.name || ""]);
  ws.addRow(["Project Number", project.number || ""]);
  ws.addRow(["Generated", new Date().toISOString().slice(0, 10)]);
  ws.addRow([]);
  const hdr = ws.addRow(SUMMARY_SCHEDULE_HEADERS);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    c.alignment = { horizontal: "center", vertical: "middle" };
  });
  const dataRows = buildSummaryScheduleRows(rooms, results, configurations, isSI);
  dataRows.forEach((r) => ws.addRow(r));
  const totalQty = dataRows.reduce((s, r) => s + (parseInt(r[3], 10) || 0), 0);
  const totalRow = ws.addRow(["", "", "Total Units", totalQty]);
  totalRow.font = { bold: true };
  totalRow.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  });
  ws.columns = [{ width: 12 }, { width: 22 }, { width: 12 }, { width: 10 }];
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(
    blob,
    `ZAMBA_${safeFilename(project.name)}_Schedule_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}


